import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../../../../../environments/environment';
import {AbstractHttpService} from '../../../../../service/abstract-http-service';
import {
  OfferRuleWorkflowAcceptance,
  OfferRuleWorkflowAggregateCollection,
  OfferRuleWorkflowBulkRequest,
  OfferRuleWorkflowCommandStatusResponse
} from '../models/offer-rule-workflow-api.model';
import {
  parseOfferRuleWorkflowAcceptance,
  parseOfferRuleWorkflowAggregateCollection,
  parseOfferRuleWorkflowCommandStatus
} from '../utilities/offer-rule-workflow.mapper';

const aggregateApiPath = '/api/offer-rule-workflow';
const bulkApiPath = `${aggregateApiPath}/bulk`;
const commandStatusPathPattern = /^\/api\/offer-rule-workflow\/commands\/([1-9]\d*)$/;

@Injectable({
  providedIn: 'root'
})
export class OfferRuleWorkflowService extends AbstractHttpService
{
  private readonly requestOptions = {
    headers: this.acceptJsonHeaders,
    withCredentials: true
  };

  constructor(private readonly httpClient: HttpClient)
  {
    super();
  }

  loadAggregates(accountId: number): Observable<OfferRuleWorkflowAggregateCollection>
  {
    this.validateIdentifier(accountId, 'accountId');
    const params = new HttpParams().set('accountId', accountId.toString());
    const response = this.httpClient.get<unknown>(
      this.buildApiUrl(aggregateApiPath),
      {...this.requestOptions, params}
    ).pipe(map((value) => parseOfferRuleWorkflowAggregateCollection(value)));

    return response;
  }

  submitBulk(request: OfferRuleWorkflowBulkRequest): Observable<OfferRuleWorkflowAcceptance>
  {
    this.validateIdentifier(request.accountId, 'accountId');
    const options = {
      headers: this.contentTypeJsonHeaders.set('Accept', 'application/json'),
      withCredentials: true
    };
    const response = this.httpClient.post<unknown>(
      this.buildApiUrl(bulkApiPath),
      request,
      options
    ).pipe(map((value) => parseOfferRuleWorkflowAcceptance(value)));

    return response;
  }

  loadCommandStatus(
    statusUri: string,
    commandId: number
  ): Observable<OfferRuleWorkflowCommandStatusResponse>
  {
    this.validateIdentifier(commandId, 'commandId');
    const statusUrl = this.validateStatusUri(statusUri, commandId);
    const response = this.httpClient.get<unknown>(statusUrl, this.requestOptions)
      .pipe(map((value) => parseOfferRuleWorkflowCommandStatus(value)));

    return response;
  }

  validateStatusUri(statusUri: string, commandId: number): string
  {
    this.validateIdentifier(commandId, 'commandId');
    const apiOrigin = new URL(`${this.buildApiOrigin()}/`).origin;
    const parsedUrl = new URL(statusUri, `${apiOrigin}/`);
    const match = commandStatusPathPattern.exec(parsedUrl.pathname);
    const resolvedCommandId = match?.[1] ?? '';
    const validUri = parsedUrl.origin === apiOrigin
      && parsedUrl.search === ''
      && parsedUrl.hash === ''
      && resolvedCommandId === commandId.toString();
    if (!validUri) {
      throw new RangeError('The command status URI is invalid.');
    }

    return parsedUrl.toString();
  }

  private validateIdentifier(identifier: number, name: string): void
  {
    if (!Number.isSafeInteger(identifier) || identifier <= 0) {
      throw new RangeError(`${name} must be a positive safe integer.`);
    }
  }

  private buildApiUrl(path: string): string
  {
    const url = `${this.buildApiOrigin()}${path}`;

    return url;
  }

  private buildApiOrigin(): string
  {
    const protocol = window.location.protocol;
    const origin = `${protocol}//${environment.autoDomain}:${environment.autoPort}`;

    return origin;
  }
}
